<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import CodeForm from '@/components/views/manage-account/Login/CodeForm.vue'
import ResendButton from '@/components/views/manage-account/Login/ResendButton.vue'
import { LoginMethod } from '@/types'
import { SET_FORM_SUBMITTED_STATUS } from '@/store/mutation-types'
import envelopeMagicLink from '@/assets/images/envelope-magiclink.svg'
import envelopeCode from '@/assets/images/envelope-code.svg'

const store = useStore()
const router = useRouter()

const header = ref('ManageAccount.Login.End.Header')
const para = ref('ManageAccount.Login.End.Paragraph')

const loginMethod = computed(() => store.state.auth.loginMethod)
const authEmail = computed(() => store.state.auth.authEmail)
const isSignup = computed(() => store.state.auth.authIntent === 'signup')
const isMagicLink = computed(() => loginMethod.value === LoginMethod.LINK)
const envelopeImage = computed(() =>
  isMagicLink.value ? envelopeMagicLink : envelopeCode
)

const switchMethodLinkText = computed(() =>
  isMagicLink.value
    ? 'ManageAccount.Login.Start.SwitchToCode'
    : 'ManageAccount.Login.Start.SwitchToMagicLink'
)

onMounted(() => {
  if (isMagicLink.value) {
    header.value = isSignup.value
      ? 'ManageAccount.Signup.End.MagicLinkHeader'
      : 'ManageAccount.Login.End.MagicLinkHeader'
    para.value = isSignup.value
      ? 'ManageAccount.Signup.End.MagicLinkParagraph'
      : 'ManageAccount.Login.End.MagicLinkParagraph'
  } else if (isSignup.value) {
    header.value = 'ManageAccount.Signup.End.Header'
    para.value = 'ManageAccount.Signup.End.Paragraph'
  }
})

/** Navigate to the alternate method URL so support can deep-link magic link. */
const switchLoginMethod = () => {
  store.commit(SET_FORM_SUBMITTED_STATUS, false)
  if (isSignup.value) {
    router.push({ name: isMagicLink.value ? 'Signup' : 'SignupMagicLink' })
    return
  }
  router.push({ name: isMagicLink.value ? 'Login' : 'LoginMagicLink' })
}
</script>

<template>
  <div class="sa-auth-step sa-auth-step--arrive">
    <header class="sa-auth-step__header">
      <img
        :src="envelopeImage"
        class="sa-auth-step__illustration"
        alt=""
        width="96"
        height="76"
      />
      <p class="sa-auth-step__eyebrow">
        {{
          isSignup
            ? $t('ManageAccount.Signup.End.Eyebrow')
            : $t('ManageAccount.Login.End.Eyebrow')
        }}
      </p>
      <h1 class="sa-auth-step__title">{{ $t(header) }}</h1>
      <i18n-t
        :keypath="para"
        tag="p"
        class="sa-auth-step__lead"
        scope="global"
      >
        <template #email>
          <strong>{{ authEmail }}</strong>
        </template>
      </i18n-t>
    </header>

    <CodeForm v-if="!isMagicLink" />

    <ResendButton v-if="!isMagicLink" />

    <i18n-t
      v-if="isMagicLink"
      keypath="ManageAccount.Login.End.NoteWithResend"
      tag="p"
      class="sa-auth__note"
      scope="global"
    >
      <template #resend>
        <ResendButton variant="link" />
      </template>
    </i18n-t>
    <p v-else class="sa-auth__note">
      {{ $t('ManageAccount.Login.End.Note') }}
    </p>

    <p class="sa-auth__alt">
      {{ $t('ManageAccount.Login.Start.TroubleSigningIn') }}
      <button
        type="button"
        class="sa-auth__alt-btn"
        data-testid="login-link-method"
        @click.prevent="switchLoginMethod"
      >
        {{ $t(switchMethodLinkText) }}
      </button>
    </p>
  </div>
</template>
